/**
 * HealthIndicator — indicador permanente de salud del sistema en el header (TopBar).
 *
 * Punto de color verde/amarillo/rojo según el `overall` de /api/admin/health-overview
 * (incluye el Monitor de Precios y todas las demás secciones). Enlaza al panel completo.
 * Poll cada 5 min, mismo endpoint que el HealthBanner del panel principal.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type Light = 'green' | 'yellow' | 'red';

const POLL_MS = 5 * 60_000;

export function HealthIndicator() {
  const [overall, setOverall] = useState<Light | null>(null);

  useEffect(() => {
    let aborted = false;
    async function fetchHealth() {
      try {
        const res = await fetch('/api/admin/health-overview', { credentials: 'include' });
        if (!res.ok) return;
        const json = (await res.json()) as { data?: { overall?: Light } };
        if (!aborted) setOverall(json?.data?.overall ?? null);
      } catch {
        // silencioso — el indicador no es crítico
      }
    }
    fetchHealth();
    const id = setInterval(fetchHealth, POLL_MS);
    return () => {
      aborted = true;
      clearInterval(id);
    };
  }, []);

  if (!overall) return null;

  const color =
    overall === 'red' ? 'bg-red-500' : overall === 'yellow' ? 'bg-yellow-400' : 'bg-green-500';
  const label =
    overall === 'red'
      ? 'Sistema con problemas — ver salud'
      : overall === 'yellow'
        ? 'Sistema con avisos — ver salud'
        : 'Sistema OK';

  return (
    <Link
      to="/admin/health"
      title={label}
      aria-label={label}
      className="flex items-center justify-center p-2 rounded-lg hover:bg-[var(--color-surface-hover)]"
    >
      <span
        className={`inline-block w-2.5 h-2.5 rounded-full ${color} ${
          overall !== 'green' ? 'animate-pulse' : ''
        }`}
      />
    </Link>
  );
}
